import { useCallback, useRef, useState } from "react";

import { httpClient } from "api/httpClient";

const GS1_RESOLVE_TIMEOUT_MS = 10000;

// Resolves one scan at a time. `tryScan` is synchronous: returns false if a scan
// is already being resolved (the guard is a ref, so same-tick scans can't slip
// past async state), otherwise it locks, returns true, and runs the work in the
// background — resolving the raw scan to canonical { sscc, gtin } via
// POST /gs1/resolve (with a timeout) and running the caller's follow-up under
// the same lock.
export const useGS1BarcodeResolver = () => {
	const runningRef = useRef(false);
	const [isResolving, setIsResolving] = useState(false);

	const tryScan = useCallback((rawCode, onResolved) => {
		if (runningRef.current) return false;
		runningRef.current = true;
		setIsResolving(true);
		(async () => {
			const controller = new AbortController();
			const timer = setTimeout(() => controller.abort(), GS1_RESOLVE_TIMEOUT_MS);
			try {
				let result = null;
				let error = null;
				try {
					const { json } = await httpClient("gs1/resolve", {
						method: "POST",
						body: JSON.stringify({ raw_code: rawCode }),
						signal: controller.signal,
					});
					result = json;
				} catch (e) {
					error = e;
				}
				await onResolved({ result, error });
			} catch {
				// onResolved threw — lock still released in finally
			} finally {
				clearTimeout(timer);
				runningRef.current = false;
				setIsResolving(false);
			}
		})();
		return true;
	}, []);

	return { tryScan, isResolving };
};
